import { Link } from 'react-router'
import { ChevronRight, Landmark, Palmtree, Plane } from 'lucide-react'
import { CurrencyValue, DateValue, EmptyState, ErrorState, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { monthName, PROVISION_TYPE_LABELS, useEmployeeProvisions } from '@/features/payroll'
import type { ProvisionBalanceView, ProvisionType } from '@/features/payroll'

const ICONS: Partial<Record<ProvisionType, typeof Landmark>> = {
  GRATUITY: Landmark,
  LEAVE_SALARY: Palmtree,
  AIR_TICKET: Plane,
}

const ORDER: ProvisionType[] = ['GRATUITY', 'LEAVE_SALARY', 'AIR_TICKET']

/**
 * One balance, and a way into it.
 *
 * <p>The whole card is a link rather than a "details" button in a corner: the number is what
 * somebody is looking at when they want to know where it came from, so the number is what
 * they should be able to click. The chevron says so without adding another control.
 */
function BalanceCard({
  employeeId,
  type,
  balance,
}: {
  employeeId: string
  type: ProvisionType
  balance: ProvisionBalanceView | undefined
}) {
  const Icon = ICONS[type] ?? Landmark
  return (
    <Link
      to={`/employees/${employeeId}/benefits/${type.toLowerCase()}`}
      className="block rounded-[var(--radius-card)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
    >
      <Card className="h-full p-4 transition-colors hover:border-[var(--color-primary)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-[var(--color-body)]">{PROVISION_TYPE_LABELS[type]}</p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
            {balance
              ? <CurrencyValue value={balance.closingBalance} currency={balance.currency ?? undefined} />
              : '—'}
          </p>
        </div>
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </div>
      <p className="mt-2 text-xs text-[var(--color-muted)]">
        {balance
          ? <>As of end of {monthName(balance.periodYear, balance.periodMonth)} · last month added{' '}
            <CurrencyValue value={balance.lastAccrual} currency={balance.currency ?? undefined} /></>
          : 'Nothing accrued yet. Run the monthly provision accrual to build this balance.'}
      </p>
      <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)]">
        Calculation and history
        <ChevronRight className="size-3" aria-hidden="true" />
      </span>
      {balance?.basisNote && (
        <p className="mt-1 text-xs text-[var(--color-muted)]">{balance.basisNote}</p>
      )}
      </Card>
    </Link>
  )
}

/**
 * Gratuity, leave salary and air ticket — what payroll owes this person and has not yet paid.
 *
 * <p>Figures are the provision ledger's latest closing balances, so they match the Provisions
 * report exactly. They are as current as the last accrual run, and each card says which
 * month that was rather than passing an older figure off as today's.
 */
export function EntitlementsTab({ employeeId }: { employeeId: string }) {
  const provisions = useEmployeeProvisions(employeeId)

  if (provisions.isPending) return <Skeleton className="h-48" />
  if (provisions.isError) {
    return <ErrorState error={provisions.error} onRetry={() => void provisions.refetch()} />
  }

  const { balances, airTicketEntitlement: entitlement, airTicketClaims: claims } = provisions.data
  const byType = new Map(balances.map((balance) => [balance.provisionType, balance]))

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        {ORDER.map((type) => (
          <BalanceCard
            key={type}
            employeeId={employeeId}
            type={type}
            balance={byType.get(type)}
          />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
            <Plane className="size-4" aria-hidden="true" />
            Air ticket entitlement
          </h3>
          {!entitlement && (
            <p className="text-sm text-[var(--color-muted)]">
              No air ticket entitlement applies to this person's grade or contract type today.
            </p>
          )}
          {entitlement && (
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Frequency</dt>
                <dd className="text-sm text-[var(--color-heading)]">
                  Every {entitlement.frequencyMonths} months
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Class</dt>
                <dd className="text-sm capitalize text-[var(--color-heading)]">
                  {entitlement.cabinClass.toLowerCase().replace(/_/g, ' ')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Travellers</dt>
                <dd className="text-sm text-[var(--color-heading)]">
                  {entitlement.adultCount} adult{entitlement.adultCount === 1 ? '' : 's'}
                  {entitlement.childCount > 0
                    ? `, ${entitlement.childCount} child${entitlement.childCount === 1 ? '' : 'ren'}`
                    : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Estimated cost</dt>
                <dd className="text-sm text-[var(--color-heading)]">
                  <CurrencyValue value={entitlement.estimatedCost} currency={entitlement.currency ?? undefined} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Monthly accrual</dt>
                <dd className="text-sm text-[var(--color-heading)]">
                  <CurrencyValue value={entitlement.monthlyAccrual} currency={entitlement.currency ?? undefined} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Can be paid as cash</dt>
                <dd className="text-sm text-[var(--color-heading)]">{entitlement.encashable ? 'Yes' : 'No'}</dd>
              </div>
            </dl>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">Ticket claims</h3>
          {claims.length === 0 && (
            <EmptyState
              icon={Plane}
              title="No tickets claimed"
              description="Claims and encashments appear here once payroll records them."
            />
          )}
          {claims.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-start text-xs text-[var(--color-muted)]">
                    <th className="py-2 pe-3 text-start font-medium">Claimed on</th>
                    <th className="py-2 pe-3 text-start font-medium">Type</th>
                    <th className="py-2 pe-3 text-end font-medium">Amount</th>
                    <th className="py-2 text-start font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => (
                    <tr key={claim.id} className="border-b border-[var(--color-border)] last:border-0">
                      <td className="py-2 pe-3"><DateValue value={claim.claimedOn} /></td>
                      <td className="py-2 pe-3 capitalize">{claim.claimType.toLowerCase().replace(/_/g, ' ')}</td>
                      <td className="py-2 pe-3 text-end tabular-nums">
                        <CurrencyValue value={claim.amount} currency={claim.currency ?? undefined} />
                      </td>
                      <td className="py-2"><StatusBadge status={claim.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
